/**
 * KnowledgeList
 *
 * Filterable list of domain knowledge entries.
 *
 * Part of Story 30: Domain Knowledge Refinement System
 *
 * Date: December 18, 2025
 */

import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Filter,
  RefreshCw,
  Plus,
  Search,
  BookOpen,
  Loader2,
  AlertCircle,
} from 'lucide-react';
import { toast } from '@/components/ui/use-toast';
import { useSite } from '@/contexts/SiteContext';
import { KnowledgeCard } from './KnowledgeCard';
import {
  getKnowledgeList,
  DomainKnowledge,
  KnowledgeFilters,
  KnowledgeType,
  KnowledgeStatus,
  ScopeLevel,
  ResearchStatus,
} from '@/services/domainKnowledgeService';

interface KnowledgeListProps {
  onView: (knowledge: DomainKnowledge) => void;
  onEdit?: (knowledge: DomainKnowledge) => void;
  onCreate?: () => void;
  refreshTrigger?: number;
}

const KNOWLEDGE_TYPES: { value: KnowledgeType | 'all'; label: string }[] = [
  { value: 'all', label: 'All Types' },
  { value: 'terminology', label: 'Terminology' },
  { value: 'specification', label: 'Specification' },
  { value: 'context', label: 'Context' },
  { value: 'expertise_signal', label: 'Expertise Signal' },
  { value: 'validation_rule', label: 'Validation Rule' },
];

const STATUSES: { value: KnowledgeStatus | 'all'; label: string }[] = [
  { value: 'all', label: 'All Statuses' },
  { value: 'proposed', label: 'Proposed' },
  { value: 'researching', label: 'Researching' },
  { value: 'pending_review', label: 'Pending Review' },
  { value: 'approved', label: 'Approved' },
  { value: 'active', label: 'Active' },
  { value: 'deprecated', label: 'Deprecated' },
  { value: 'resolved', label: 'Resolved' },
];

const SCOPE_LEVELS: { value: ScopeLevel | 'all'; label: string }[] = [
  { value: 'all', label: 'All Scopes' },
  { value: 'sector', label: 'Sector' },
  { value: 'category', label: 'Category' },
  { value: 'brand', label: 'Brand' },
  { value: 'attribute', label: 'Attribute' },
  { value: 'product', label: 'Product' },
];

const RESEARCH_STATUSES: { value: ResearchStatus | 'all'; label: string }[] = [
  { value: 'all', label: 'All Research' },
  { value: 'pending', label: 'Pending' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'completed', label: 'Completed' },
  { value: 'failed', label: 'Failed' },
];

export function KnowledgeList({ onView, onEdit, onCreate, refreshTrigger }: KnowledgeListProps) {
  const { selectedSite } = useSite();
  const [knowledge, setKnowledge] = useState<DomainKnowledge[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState({
    page: 1,
    totalPages: 1,
    total: 0,
    limit: 20,
  });

  const [filters, setFilters] = useState<KnowledgeFilters>({
    siteId: selectedSite?.id || undefined,
    status: undefined,
    knowledgeType: undefined,
    scopeLevel: undefined,
    researchStatus: undefined,
    page: 1,
    limit: 20,
  });

  const [searchQuery, setSearchQuery] = useState('');

  // Update site filter when site changes
  useEffect(() => {
    setFilters((prev) => ({
      ...prev,
      siteId: selectedSite?.id || undefined,
      page: 1,
    }));
  }, [selectedSite?.id]);

  // Load data when filters change or refresh triggered
  useEffect(() => {
    loadKnowledge();
  }, [filters, refreshTrigger]);

  const loadKnowledge = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await getKnowledgeList(filters);

      if (response.success && response.data) {
        setKnowledge(response.data.items);
        setPagination({
          page: response.data.page,
          totalPages: response.data.totalPages,
          total: response.data.total,
          limit: filters.limit || 20,
        });
      } else {
        setError(response.error || 'Failed to load knowledge');
        toast({
          title: 'Error',
          description: response.error || 'Failed to load knowledge',
          variant: 'destructive',
        });
      }
    } catch (err) {
      console.error('[KnowledgeList] Error loading:', err);
      setError('Failed to load knowledge');
      toast({
        title: 'Error',
        description: 'Failed to load domain knowledge',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key: keyof KnowledgeFilters, value: string) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value === 'all' ? undefined : value,
      page: 1,
    }));
  };

  const handlePageChange = (page: number) => {
    setFilters((prev) => ({ ...prev, page }));
  };

  const handleRefresh = () => {
    loadKnowledge();
  };

  // Filter by search query (client-side)
  const filteredKnowledge = searchQuery
    ? knowledge.filter(
        (k) =>
          k.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
          k.knowledge_text?.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : knowledge;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <BookOpen className="h-5 w-5" />
          <h2 className="text-lg font-semibold">Domain Knowledge</h2>
          <Badge variant="secondary">{pagination.total} entries</Badge>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={handleRefresh}>
            <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          {onCreate && (
            <Button size="sm" onClick={onCreate}>
              <Plus className="h-4 w-4 mr-1" />
              Add Knowledge
            </Button>
          )}
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm flex items-center gap-2">
            <Filter className="h-4 w-4" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3">
            {/* Search */}
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search title or content..."
                  className="pl-8"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
            </div>

            {/* Type Filter */}
            <Select
              value={filters.knowledgeType || 'all'}
              onValueChange={(v) => handleFilterChange('knowledgeType', v)}
            >
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                {KNOWLEDGE_TYPES.map((t) => (
                  <SelectItem key={t.value} value={t.value}>
                    {t.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Status Filter */}
            <Select
              value={filters.status || 'all'}
              onValueChange={(v) => handleFilterChange('status', v)}
            >
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                {STATUSES.map((s) => (
                  <SelectItem key={s.value} value={s.value}>
                    {s.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Scope Filter */}
            <Select
              value={filters.scopeLevel || 'all'}
              onValueChange={(v) => handleFilterChange('scopeLevel', v)}
            >
              <SelectTrigger className="w-[130px]">
                <SelectValue placeholder="Scope" />
              </SelectTrigger>
              <SelectContent>
                {SCOPE_LEVELS.map((s) => (
                  <SelectItem key={s.value} value={s.value}>
                    {s.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Research Status Filter */}
            <Select
              value={filters.researchStatus || 'all'}
              onValueChange={(v) => handleFilterChange('researchStatus', v)}
            >
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="Research" />
              </SelectTrigger>
              <SelectContent>
                {RESEARCH_STATUSES.map((s) => (
                  <SelectItem key={s.value} value={s.value}>
                    {s.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : error ? (
        <Card>
          <CardContent className="flex items-center justify-center py-12">
            <div className="text-center">
              <AlertCircle className="h-10 w-10 text-destructive mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">{error}</p>
              <Button variant="outline" size="sm" className="mt-4" onClick={handleRefresh}>
                Try Again
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : filteredKnowledge.length === 0 ? (
        <Card>
          <CardContent className="flex items-center justify-center py-12">
            <div className="text-center">
              <BookOpen className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">
                {searchQuery ? 'No matching knowledge found' : 'No domain knowledge entries yet'}
              </p>
              {onCreate && !searchQuery && (
                <Button size="sm" className="mt-4" onClick={onCreate}>
                  <Plus className="h-4 w-4 mr-1" />
                  Add First Entry
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Knowledge Grid */}
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {filteredKnowledge.map((k) => (
              <KnowledgeCard
                key={k.id}
                knowledge={k}
                onView={onView}
                onEdit={onEdit}
              />
            ))}
          </div>

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 pt-4">
              <Button
                variant="outline"
                size="sm"
                disabled={pagination.page <= 1}
                onClick={() => handlePageChange(pagination.page - 1)}
              >
                Previous
              </Button>
              <span className="text-sm text-muted-foreground">
                Page {pagination.page} of {pagination.totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={pagination.page >= pagination.totalPages}
                onClick={() => handlePageChange(pagination.page + 1)}
              >
                Next
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
